import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { ChevronRight, Info, Plus, Sparkles, Trash2 } from "lucide-react";
import { PageHeader } from "@/components/AppShell";
import { Sheet } from "@/components/Sheet";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  EmptyState,
  Field,
  DurationInput,
  NumberStepper,
  PageLoader,
  Segmented,
  Select,
  Stat,
  TextField,
  Toggle,
  cx,
} from "@/components/ui";
import { Avatar, DayPicker, IconPicker } from "@/components/bits";
import { api, type ApiError } from "@/lib/api";
import { useAction, useIntegrations, useRewardTypes } from "@/lib/queries";
import { useSession } from "@/lib/session";
import { KIND_LABELS } from "@/lib/format";
import {
  describeSchedule,
  formatUnits,
  isConvertible,
  pointsForUnits,
  unitsForPoints,
  type RewardKind,
} from "@shared/economy";
import { formatDuration } from "@shared/duration";
import { ALL_DAYS, NOT_SCHOOL_NIGHTS, WEEKENDS, describeDays, formatHHMM, parseHHMM } from "@shared/time";
import type { AdviceResponse, CapSuggestionDTO, FamilyDTO, RewardTypeDTO } from "@shared/types";

// ---------------------------------------------------------------------------------------------
// Small shared bits for the plan editors (Jobs, Store and Discipline import these too).
// ---------------------------------------------------------------------------------------------

/**
 * Ask the advisor something. Errors become toasts like `useAction`, but nothing is saved,
 * so there's no need to refresh every query afterwards.
 */
export function useAdvice<TVars = void, TResult = unknown>(
  fn: (vars: TVars) => Promise<TResult>,
  onSuccess?: (result: TResult) => void,
) {
  const toast = useToast();
  return useMutation<TResult, ApiError, TVars>({
    mutationFn: fn,
    onSuccess: (result) => onSuccess?.(result),
    onError: (err) => toast.show(err.message, "error"),
  });
}

/** "Coin" for 1, "Coins" otherwise (good enough for the preset currency names). */
export function currencyWord(n: number, family: Pick<FamilyDTO, "currencyName">): string {
  const name = family.currencyName;
  return Math.abs(n) === 1 && /[^s]s$/i.test(name) ? name.slice(0, -1) : name;
}

const num = (n: number) => n.toLocaleString(undefined, { maximumFractionDigits: 2 });

/** "≈ $2.50 cash · 25 min Screen time" for an amount of points, using active cash/time rewards. */
export function useEquivalents(points: number): string | null {
  const { data } = useRewardTypes();
  if (!data || !(points > 0)) return null;
  const parts = data.rewardTypes
    .filter((rt) => rt.active && isConvertible(rt))
    .map((rt) => `${formatUnits(unitsForPoints(points, rt.pointsPerUnit!), rt)} ${rt.kind === "cash" ? rt.name.toLowerCase() : rt.name}`);
  return parts.length ? `≈ ${parts.join(" · ")}` : null;
}

export function RewardEquivalents({ points, className }: { points: number; className?: string }) {
  const text = useEquivalents(points);
  if (!text) return null;
  return (
    <p className={cx("inline-flex rounded-xl bg-coin-soft px-3 py-1.5 text-sm font-bold text-coin-ink", className)}>{text}</p>
  );
}

/** Advice comes from the built-in rules (there's no AI in the app). */
export function AdvisorSource({ note }: { source: "built-in"; note?: string }) {
  return (
    <div className="flex flex-wrap items-center gap-2 text-xs text-muted">
      <Badge tone="violet">
        <Sparkles className="size-3" /> Built-in suggestions
      </Badge>
      {note && <span>{note}</span>}
    </div>
  );
}

/** A friendly waiting state while suggestions load. */
export function AdvisorThinking({ title, children }: { title: ReactNode; children?: ReactNode }) {
  return (
    <div className="flex flex-col items-center gap-3 rounded-3xl bg-violet-soft px-6 py-10 text-center" role="status">
      <span className="flex size-14 items-center justify-center rounded-full bg-card shadow-card">
        <Sparkles className="size-7 animate-pulse text-violet" />
      </span>
      <p className="font-extrabold">{title}</p>
      <p className="max-w-xs text-sm text-muted">
        {children ?? "Just a moment…"}
      </p>
    </div>
  );
}

/** A big, tappable "ask the advisor" card. */
export function AdvisorCard({ title, children, onClick }: { title: ReactNode; children?: ReactNode; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 rounded-3xl bg-violet-soft p-4 text-left ring-1 ring-violet/20 transition hover:ring-violet/50 active:scale-[0.99]"
    >
      <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-card text-violet shadow-card">
        <Sparkles className="size-6" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block font-extrabold">{title}</span>
        {children && <span className="mt-0.5 block text-sm text-muted">{children}</span>}
      </span>
      <ChevronRight className="size-5 shrink-0 text-violet" />
    </button>
  );
}

/** Two-tap delete for sheet footers: the first tap arms it, the second confirms. */
export function ConfirmDelete({
  onConfirm,
  loading,
  label = "Delete",
  confirmLabel = "Delete?",
}: {
  onConfirm: () => void;
  loading?: boolean;
  label?: string;
  confirmLabel?: string;
}) {
  const [armed, setArmed] = useState(false);
  useEffect(() => {
    if (!armed) return;
    const t = setTimeout(() => setArmed(false), 4000);
    return () => clearTimeout(t);
  }, [armed]);
  if (armed || loading) {
    return (
      <Button variant="danger" onClick={onConfirm} loading={loading} className="shrink-0">
        {confirmLabel}
      </Button>
    );
  }
  return (
    <Button variant="secondary" aria-label={label} title={label} onClick={() => setArmed(true)} className="shrink-0">
      <Trash2 className="size-4 text-danger" />
    </Button>
  );
}

/** "10 Coins = $1" or "1 Coin = 1 min". */
export function describeConversion(
  rt: Pick<RewardTypeDTO, "kind" | "pointsPerUnit" | "unitLabel">,
  family: Pick<FamilyDTO, "currencyName">,
): string | null {
  const ppu = rt.pointsPerUnit;
  if (!ppu || (rt.kind !== "cash" && rt.kind !== "time")) return null;
  if (rt.kind === "cash") return `${num(ppu)} ${currencyWord(ppu, family)} = ${formatUnits(1, rt).replace(/\.00$/, "")}`;
  if (ppu >= 1) return `${num(ppu)} ${currencyWord(ppu, family)} = 1 min`;
  return `1 ${currencyWord(1, family)} = ${num(Math.round((1 / ppu) * 100) / 100)} min`;
}

/** "Max 5 hrs/week · 1 hr 30 min/day" */
export function describeCaps(rt: Pick<RewardTypeDTO, "kind" | "unitLabel" | "weeklyCap" | "dailyCap">): string | null {
  const parts: string[] = [];
  if (rt.weeklyCap !== null) parts.push(`${formatUnits(rt.weeklyCap, rt)}/week`);
  if (rt.dailyCap !== null) parts.push(`${formatUnits(rt.dailyCap, rt)}/day`);
  return parts.length ? `Max ${parts.join(" · ")}` : null;
}

// ---------------------------------------------------------------------------------------------
// Rewards editor
// ---------------------------------------------------------------------------------------------

type Kind = "cash" | "time";

interface Draft {
  id?: string;
  name: string;
  icon: string;
  kind: RewardKind;
  unitLabel: string;
  pointsPerUnit: number;
  weeklyCap: number | null;
  dailyCap: number | null;
  allowedDays: number;
  startTime: string;
  endTime: string;
  note: string;
  requiresApproval: boolean;
  active: boolean;
  integrationId: string | null;
}

interface RewardTemplate {
  kind: Kind;
  name: string;
  icon: string;
  pointsPerUnit: number;
  requiresApproval: boolean;
  weeklyCap?: number;
  dailyCap?: number;
}

const TEMPLATES: RewardTemplate[] = [
  { kind: "cash", name: "Cash", icon: "💵", pointsPerUnit: 10, requiresApproval: true },
  { kind: "time", name: "Screen time", icon: "📺", pointsPerUnit: 1, requiresApproval: false, weeklyCap: 300, dailyCap: 90 },
  { kind: "time", name: "Game time", icon: "🎮", pointsPerUnit: 1, requiresApproval: false, weeklyCap: 240, dailyCap: 60 },
];

const REWARD_ICONS = ["💵", "🪙", "🏦", "📺", "🎮", "📱", "💻", "🎧", "🎬", "📖", "🧩", "⏱️", "🎁", "⭐"];
const SCHOOL_NIGHTS_NOTE = "No screen time on school nights";

function fromTemplate(t: RewardTemplate): Draft {
  return {
    name: t.name,
    icon: t.icon,
    kind: t.kind,
    unitLabel: t.kind === "cash" ? "$" : "min",
    pointsPerUnit: t.pointsPerUnit,
    weeklyCap: t.weeklyCap ?? null,
    dailyCap: t.dailyCap ?? null,
    allowedDays: ALL_DAYS,
    startTime: "",
    endTime: "",
    note: "",
    requiresApproval: t.requiresApproval,
    active: true,
    integrationId: null,
  };
}

const newDraft = (kind: Kind) => fromTemplate(TEMPLATES.find((t) => t.kind === kind)!);

function toDraft(rt: RewardTypeDTO): Draft {
  return {
    id: rt.id,
    name: rt.name,
    icon: rt.icon,
    kind: rt.kind,
    unitLabel: rt.unitLabel,
    pointsPerUnit: rt.pointsPerUnit ?? (rt.kind === "cash" ? 10 : 1),
    weeklyCap: rt.weeklyCap,
    dailyCap: rt.dailyCap,
    allowedDays: rt.schedule?.allowedDays ?? ALL_DAYS,
    startTime: rt.schedule?.startTime ?? "",
    endTime: rt.schedule?.endTime ?? "",
    note: rt.schedule?.note ?? "",
    requiresApproval: rt.requiresApproval,
    active: rt.active,
    integrationId: rt.integrationId,
  };
}

const isConvertibleKind = (k: RewardKind): k is Kind => k === "cash" || k === "time";

function toBody(d: Draft) {
  const base = { name: d.name.trim(), icon: d.icon, requiresApproval: d.requiresApproval, active: d.active };
  if (!isConvertibleKind(d.kind)) return base;
  const isTime = d.kind === "time";
  const hasSchedule = isTime && (d.allowedDays !== ALL_DAYS || !!d.startTime || !!d.endTime || !!d.note.trim());
  return {
    ...base,
    kind: d.kind,
    unitLabel: d.kind === "cash" ? "$" : "min",
    pointsPerUnit: d.pointsPerUnit,
    weeklyCap: isTime ? d.weeklyCap : null,
    dailyCap: isTime ? d.dailyCap : null,
    schedule: hasSchedule
      ? { allowedDays: d.allowedDays, startTime: d.startTime || null, endTime: d.endTime || null, note: d.note.trim() || null }
      : null,
    integrationId: isTime ? d.integrationId : null,
  };
}

function validate(d: Draft): string | null {
  if (!d.name.trim()) return "Give it a name";
  if (isConvertibleKind(d.kind) && !(d.pointsPerUnit > 0)) return "Set a rate above zero";
  if (d.kind === "time") {
    if (d.allowedDays === 0) return "Pick at least one day it can be used";
    const start = parseHHMM(d.startTime);
    const end = parseHHMM(d.endTime);
    if (start !== null && end !== null && start >= end) return "“Not before” must be earlier than “Done by”";
    if (d.weeklyCap !== null && d.dailyCap !== null && d.dailyCap > d.weeklyCap) return "The daily limit is bigger than the weekly one";
  }
  return null;
}

function CapField({
  label,
  per,
  value,
  onChange,
  fallback,
  max,
}: {
  label: string;
  per: string;
  value: number | null;
  onChange: (v: number | null) => void;
  fallback: number;
  max: number;
}) {
  return (
    <Field label={label} hint={value === null ? "No limit — kids can use as much as they've banked." : `${formatDuration(value)} per ${per}`}>
      <Segmented
        value={value === null ? "none" : "set"}
        onChange={(v) => onChange(v === "none" ? null : (value ?? fallback))}
        options={[
          { value: "none", label: "No limit" },
          { value: "set", label: "Set a limit" },
        ]}
      />
      {value !== null && (
        <div className="pt-2">
          <DurationInput label={label} value={value} onChange={onChange} min={0} max={max} step={15} />
        </div>
      )}
    </Field>
  );
}

function RewardForm({ draft, setDraft }: { draft: Draft; setDraft: (d: Draft) => void }) {
  const { family } = useSession();
  const { data: integrationData } = useIntegrations();
  const integrations = integrationData?.integrations ?? [];
  const set = (patch: Partial<Draft>) => setDraft({ ...draft, ...patch });
  const convertible = isConvertibleKind(draft.kind);
  const isTime = draft.kind === "time";
  const currency = family.currencyName;

  const switchKind = (kind: Kind) => {
    if (kind === draft.kind) return;
    const base = newDraft(kind);
    const pristine = TEMPLATES.some((t) => t.name === draft.name && t.icon === draft.icon);
    setDraft(
      pristine
        ? { ...base, active: draft.active }
        : { ...draft, kind, unitLabel: base.unitLabel, pointsPerUnit: base.pointsPerUnit, weeklyCap: base.weeklyCap, dailyCap: base.dailyCap },
    );
  };

  const samplePoints = convertible ? pointsForUnits(isTime ? 60 : 5, draft.pointsPerUnit || 1) : 0;
  const icons = REWARD_ICONS.includes(draft.icon) ? REWARD_ICONS : [draft.icon, ...REWARD_ICONS];

  return (
    <div className="space-y-5">
      {!draft.id ? (
        <Field
          label="Type"
          hint={
            isTime
              ? "Kids bank minutes (screen time, gaming) and use them later, within your limits."
              : `Kids cash out ${currency} for real money you pay them.`
          }
        >
          <Segmented
            value={draft.kind as Kind}
            onChange={switchKind}
            options={[
              { value: "cash", label: "💵 Cash" },
              { value: "time", label: "⏱️ Time" },
            ]}
          />
        </Field>
      ) : (
        <div className="flex items-center gap-2 rounded-2xl bg-sunken px-4 py-3 text-sm">
          <Badge tone={draft.kind === "cash" ? "coin" : "brand"}>{KIND_LABELS[draft.kind] ?? draft.kind}</Badge>
          <span className="text-muted">
            {convertible ? "The type can't change once kids are using it." : "Items, digital rewards and privileges work best in the Store."}
          </span>
        </div>
      )}

      <TextField
        label="Name"
        placeholder={isTime ? "Screen time" : "Cash"}
        value={draft.name}
        maxLength={40}
        onChange={(e) => set({ name: e.target.value })}
      />
      <Field label="Icon">
        <IconPicker value={draft.icon} onChange={(icon) => set({ icon })} choices={icons} />
      </Field>

      {convertible && (
        <Field
          label={isTime ? `${currency} per minute` : `${currency} per $1`}
          hint={`${isTime ? "An hour" : formatUnits(5, draft)} costs ${samplePoints.toLocaleString()} ${currencyWord(samplePoints, family)}.`}
        >
          <NumberStepper
            value={draft.pointsPerUnit}
            onChange={(pointsPerUnit) => set({ pointsPerUnit })}
            min={isTime ? 0.5 : 1}
            max={100_000}
            step={isTime ? 0.5 : 1}
            suffix={family.currencyIcon}
          />
        </Field>
      )}

      {isTime && (
        <>
          <div className="space-y-4 rounded-3xl bg-sunken/60 p-4 ring-1 ring-line">
            <h3 className="font-extrabold">Limits</h3>
            <CapField
              label="Per week"
              per="week"
              value={draft.weeklyCap}
              onChange={(weeklyCap) => set({ weeklyCap })}
              fallback={300}
              max={7 * 24 * 60}
            />
            <CapField
              label="Per day"
              per="day"
              value={draft.dailyCap}
              onChange={(dailyCap) => set({ dailyCap })}
              fallback={60}
              max={24 * 60}
            />
          </div>

          <div className="space-y-4 rounded-3xl bg-sunken/60 p-4 ring-1 ring-line">
            <h3 className="font-extrabold">When it can be used</h3>
            <div className="flex flex-wrap gap-2">
              {[
                { label: "Every day", days: ALL_DAYS, note: "" },
                { label: "No school nights", days: NOT_SCHOOL_NIGHTS, note: SCHOOL_NIGHTS_NOTE },
                { label: "Weekends", days: WEEKENDS, note: "" },
              ].map((p) => (
                <button
                  key={p.label}
                  type="button"
                  aria-pressed={draft.allowedDays === p.days}
                  onClick={() =>
                    set({
                      allowedDays: p.days,
                      // Swap in the preset's note, but never overwrite a note the parent wrote.
                      note: !draft.note || draft.note === SCHOOL_NIGHTS_NOTE ? p.note : draft.note,
                    })
                  }
                  className={cx(
                    "rounded-full px-3 py-1.5 text-sm font-bold transition",
                    draft.allowedDays === p.days ? "bg-brand text-on-brand" : "bg-card text-muted ring-1 ring-line hover:text-ink",
                  )}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <DayPicker value={draft.allowedDays} onChange={(allowedDays) => set({ allowedDays })} />
            <div className="grid grid-cols-2 gap-3">
              <TextField
                label="Not before"
                type="time"
                value={draft.startTime}
                onChange={(e) => set({ startTime: e.target.value })}
              />
              <TextField label="Done by" type="time" value={draft.endTime} onChange={(e) => set({ endTime: e.target.value })} />
            </div>
            <TextField
              label="Rule (optional)"
              placeholder={SCHOOL_NIGHTS_NOTE}
              value={draft.note}
              maxLength={120}
              onChange={(e) => set({ note: e.target.value })}
              hint="Kids see this when they can't use their time."
            />
          </div>
        </>
      )}

      <Toggle
        checked={draft.requiresApproval}
        onChange={(requiresApproval) => set({ requiresApproval })}
        label="Ask me before converting"
        description={
          draft.requiresApproval
            ? `You approve each time a kid turns ${currency} into ${draft.name.trim() || "this"}.`
            : "Kids convert instantly — you'll still see it in activity."
        }
      />

      {isTime && (
        <Field
          label="Automatic enforcement (beta)"
          hint={
            <>
              Sends used time to a device or service.{" "}
              <Link to="/parent/integrations" className="font-bold text-brand">
                Manage integrations
              </Link>
            </>
          }
        >
          {integrations.length > 0 ? (
            <Select value={draft.integrationId ?? ""} onChange={(e) => set({ integrationId: e.target.value || null })}>
              <option value="">None — I'll set timers myself</option>
              {integrations.map((i) => (
                <option key={i.id} value={i.id}>
                  {i.name}
                  {i.enabled ? "" : " (paused)"}
                </option>
              ))}
            </Select>
          ) : (
            <p className="rounded-2xl bg-sunken px-4 py-3 text-sm text-muted">No integrations connected yet.</p>
          )}
        </Field>
      )}

      <Toggle
        checked={draft.active}
        onChange={(active) => set({ active })}
        label="Active"
        description="Inactive rewards are hidden from kids."
      />

      {draft.id && draft.active && (
        <p className="text-xs text-muted">
          Retiring hides this reward and removes penalty presets that use it. Past balances stay in history.
        </p>
      )}
    </div>
  );
}

function CapsSheet({
  open,
  onClose,
  timeTypes,
  result,
  loading,
  onRetry,
}: {
  open: boolean;
  onClose: () => void;
  timeTypes: RewardTypeDTO[];
  result: AdviceResponse<CapSuggestionDTO[]> | null;
  loading: boolean;
  onRetry: () => void;
}) {
  const { memberById } = useSession();
  const [targetId, setTargetId] = useState("");
  const [withSchedule, setWithSchedule] = useState(false);
  const target = timeTypes.find((t) => t.id === targetId) ?? timeTypes[0];

  const apply = useAction(
    (s: CapSuggestionDTO) => {
      const body: Record<string, unknown> = { weeklyCap: s.weeklyMinutes, dailyCap: s.dailyMinutes };
      if (withSchedule) {
        body.schedule = {
          ...(target.schedule ?? {}),
          allowedDays: s.allowedDays || ALL_DAYS,
          endTime: s.endTime ?? target.schedule?.endTime ?? null,
          note: s.scheduleNote ? s.scheduleNote.slice(0, 120) : null,
        };
      }
      return api.patch(`/rewards/${target.id}`, body);
    },
    { success: () => `Limits updated for ${target?.name ?? "time"}` },
  );

  return (
    <Sheet
      open={open}
      onClose={onClose}
      title="Suggested limits"
      size="lg"
      footer={
        result && !loading ? (
          <Button variant="secondary" block onClick={onRetry}>
            <Sparkles className="size-4" /> Ask again
          </Button>
        ) : undefined
      }
    >
      {loading || !result ? (
        <AdvisorThinking title="Working out age-appropriate limits…" />
      ) : (
        <div className="space-y-4">
          <AdvisorSource source={result.source} note={result.note} />
          {target && (
            <div className="space-y-3 rounded-3xl bg-sunken p-4">
              {timeTypes.length > 1 && (
                <Field label="Apply to">
                  <Select value={target.id} onChange={(e) => setTargetId(e.target.value)}>
                    {timeTypes.map((t) => (
                      <option key={t.id} value={t.id}>
                        {t.icon} {t.name}
                      </option>
                    ))}
                  </Select>
                </Field>
              )}
              <p className="text-sm">
                <span className="font-bold">
                  {target.icon} {target.name} now:
                </span>{" "}
                <span className="text-muted">{describeCaps(target) ?? "no limits"}</span>
              </p>
              <Toggle
                checked={withSchedule}
                onChange={setWithSchedule}
                label="Also use the suggested days & rule"
                description="Otherwise only the weekly and daily limits change."
              />
              <p className="text-xs text-muted">Limits belong to the reward, so they apply to every kid. Pick the one that fits best.</p>
            </div>
          )}
          {result.items.map((s) => {
            const kid = memberById(s.kidId);
            return (
              <Card key={s.kidId} className="p-4">
                <div className="flex items-center gap-3">
                  {kid && <Avatar member={kid} size="sm" />}
                  <div className="min-w-0 flex-1">
                    <div className="font-extrabold">{s.kidName}</div>
                    {s.age !== null && <div className="text-xs font-bold text-muted">Age {s.age}</div>}
                  </div>
                </div>
                <div className="mt-3 grid grid-cols-2 gap-2">
                  <Stat label="Per week" value={formatDuration(s.weeklyMinutes)} tone="brand" />
                  <Stat label="Per day" value={formatDuration(s.dailyMinutes)} tone="brand" />
                </div>
                <p className="mt-3 text-sm font-bold">
                  {describeDays(s.allowedDays)}
                  {s.endTime ? ` · done by ${formatHHMM(s.endTime)}` : ""}
                  {s.scheduleNote ? ` · ${s.scheduleNote}` : ""}
                </p>
                <p className="mt-1 text-sm text-muted">{s.rationale}</p>
                {target && (
                  <Button
                    variant="soft"
                    block
                    className="mt-3"
                    onClick={() => apply.mutate(s)}
                    loading={apply.isPending && apply.variables === s}
                    disabled={apply.isPending}
                  >
                    Use for {target.name}
                  </Button>
                )}
              </Card>
            );
          })}
        </div>
      )}
    </Sheet>
  );
}

export function RewardsEditor() {
  const { family } = useSession();
  const { data, isLoading } = useRewardTypes();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [capsOpen, setCapsOpen] = useState(false);
  const [caps, setCaps] = useState<AdviceResponse<CapSuggestionDTO[]> | null>(null);

  const save = useAction(
    (d: Draft) => (d.id ? api.patch(`/rewards/${d.id}`, toBody(d)) : api.post("/rewards", toBody(d))),
    { success: "Saved", onSuccess: () => setDraft(null) },
  );
  const retire = useAction((id: string) => api.del(`/rewards/${id}`), { success: "Retired", onSuccess: () => setDraft(null) });
  const suggestCaps = useAdvice(() => api.post<AdviceResponse<CapSuggestionDTO[]>>("/advisor/caps"), setCaps);

  if (isLoading || !data) return <PageLoader />;

  const list = [...data.rewardTypes].sort((a, b) => Number(b.active) - Number(a.active) || a.sortOrder - b.sortOrder);
  const timeTypes = list.filter((t) => t.active && t.kind === "time" && t.pointsPerUnit);
  const names = new Set(list.filter((t) => t.active).map((t) => t.name.toLowerCase()));
  const quick = TEMPLATES.filter((t) => !names.has(t.name.toLowerCase()));
  const hasCash = list.some((t) => t.active && t.kind === "cash");
  const currency = family.currencyName;
  const error = draft ? validate(draft) : null;

  const openCaps = () => {
    setCapsOpen(true);
    if (!caps && !suggestCaps.isPending) suggestCaps.mutate();
  };

  return (
    <div>
      <PageHeader
        title="Rewards"
        subtitle={`What ${currency} turn into`}
        back="/parent/plan"
        actions={
          <Button size="sm" onClick={() => setDraft(newDraft(hasCash ? "time" : "cash"))}>
            <Plus className="size-4" /> Add
          </Button>
        }
      />

      <div className="mt-2 flex items-start gap-3 rounded-3xl bg-coin-soft p-4">
        <span className="text-3xl" aria-hidden>
          {family.currencyIcon}
        </span>
        <div className="min-w-0">
          <p className="font-extrabold text-coin-ink">
            Kids earn {currency}. These are what {currency} turn into.
          </p>
          <p className="mt-0.5 text-sm text-muted">
            Cash and time convert at a fixed rate. Toys, treats and privileges live in the{" "}
            <Link to="/parent/plan/store" className="font-bold text-brand">
              Store
            </Link>
            .
          </p>
        </div>
      </div>

      {list.length === 0 ? (
        <div className="mt-4">
          <EmptyState
            icon="💵"
            title="No rewards yet"
            action={
              <div className="flex flex-wrap justify-center gap-2">
                <Button onClick={() => setDraft(newDraft("cash"))}>💵 Add cash</Button>
                <Button variant="secondary" onClick={() => setDraft(newDraft("time"))}>
                  📺 Add screen time
                </Button>
              </div>
            }
          >
            Most families start with cash and screen time.
          </EmptyState>
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {list.map((rt) => {
            const conversion = describeConversion(rt, family);
            const details = [describeCaps(rt), rt.kind === "time" ? describeSchedule(rt.schedule) : null].filter(Boolean);
            return (
              <Card
                key={rt.id}
                className={cx("cursor-pointer p-3 transition hover:ring-brand/40", !rt.active && "opacity-60")}
                onClick={() => setDraft(toDraft(rt))}
              >
                <div className="flex items-start gap-3">
                  <span className="flex size-12 shrink-0 items-center justify-center rounded-2xl bg-sunken text-2xl">{rt.icon}</span>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-1.5">
                      <span className="mr-0.5 font-extrabold">{rt.name}</span>
                      <Badge tone={rt.kind === "cash" ? "coin" : rt.kind === "time" ? "brand" : "neutral"}>
                        {KIND_LABELS[rt.kind] ?? rt.kind}
                      </Badge>
                      {!rt.active ? (
                        <Badge>Inactive</Badge>
                      ) : rt.requiresApproval ? (
                        <Badge tone="warn">Needs approval</Badge>
                      ) : (
                        <Badge tone="brand">Instant</Badge>
                      )}
                    </div>
                    {conversion && <div className="mt-1 text-sm font-extrabold text-coin-ink">{conversion}</div>}
                    {details.map((d) => (
                      <div key={d} className="text-sm text-muted">
                        {d}
                      </div>
                    ))}
                    {rt.integrationId && <div className="text-xs font-bold text-violet">Linked to an integration</div>}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {timeTypes.length > 0 && (
        <div className="mt-6">
          <AdvisorCard title="Not sure about limits?" onClick={openCaps}>
            ✨ Suggest caps based on your kids' ages.
          </AdvisorCard>
        </div>
      )}

      <div className="mt-4 flex items-start gap-2 rounded-2xl bg-sunken px-4 py-3 text-sm text-muted">
        <Info className="mt-0.5 size-4 shrink-0" />
        <p>
          Limits and schedules are tracked and enforced in the app today. Automatic device enforcement comes from{" "}
          <Link to="/parent/integrations" className="font-bold text-brand">
            Integrations
          </Link>
          .
        </p>
      </div>

      {quick.length > 0 && list.length > 0 && (
        <div className="mt-8">
          <h2 className="mb-2 px-1 text-lg font-extrabold">Quick add</h2>
          <div className="flex flex-wrap gap-2">
            {quick.map((t) => (
              <button
                key={t.name}
                type="button"
                onClick={() => setDraft(fromTemplate(t))}
                className="inline-flex items-center gap-1.5 rounded-full bg-card px-3 py-2 text-sm font-bold shadow-card ring-1 ring-line transition hover:ring-brand"
              >
                <span>{t.icon}</span> {t.name}
                <Plus className="size-4 text-brand" />
              </button>
            ))}
          </div>
        </div>
      )}

      <Sheet
        open={!!draft}
        onClose={() => setDraft(null)}
        title={draft?.id ? "Edit reward" : "New reward"}
        footer={
          <div>
            {error && draft?.name.trim() && <p className="mb-2 text-center text-sm font-semibold text-danger">{error}</p>}
            <div className="flex gap-2">
              {draft?.id && list.find((t) => t.id === draft.id)?.active && (
                <ConfirmDelete
                  label="Retire"
                  confirmLabel="Retire?"
                  onConfirm={() => draft.id && retire.mutate(draft.id)}
                  loading={retire.isPending}
                />
              )}
              <Button block onClick={() => draft && save.mutate(draft)} loading={save.isPending} disabled={!!error}>
                Save
              </Button>
            </div>
          </div>
        }
      >
        {draft && <RewardForm draft={draft} setDraft={setDraft} />}
      </Sheet>

      <CapsSheet
        open={capsOpen}
        onClose={() => setCapsOpen(false)}
        timeTypes={timeTypes}
        result={caps}
        loading={suggestCaps.isPending}
        onRetry={() => suggestCaps.mutate()}
      />
    </div>
  );
}
